import { FileText } from 'lucide-react';
import { useMemo, type ReactNode } from 'react';
import { Card, EmptyState, SectionHead, cn, fileMeta } from './ui/primitives';
import type { BrandAsset } from './types';

/**
 * Everything else in the kit, filtered by category.
 *
 * The old page listed non-logo files in one undifferentiated column, so a
 * 40-file kit was a scroll. The filter strip is built from the categories that
 * are actually present rather than the full `BrandFileType` enum — offering
 * "Fonts (0)" on a brand with no fonts is a control that does nothing.
 *
 * Version history and upload-new-version, which the v2 design shows per row,
 * are absent on purpose: `BrandFile` has no version field, and a "v2" pill with
 * nothing behind it is a promise the page can't keep.
 */
export function BrandAssets({
	assets,
	category,
	onCategoryChange,
	onOpen,
	renderActions,
	action,
	emptyHint,
}: {
	assets: BrandAsset[];
	/** `all`, or one of the categories present in `assets`. */
	category: string;
	onCategoryChange: (category: string) => void;
	onOpen?: (id: string) => void;
	renderActions?: (asset: BrandAsset) => ReactNode;
	action?: ReactNode;
	emptyHint?: string;
}) {
	const categories = useMemo(() => {
		const present = new Set(assets.map((a) => a.category));
		return ['all', ...Array.from(present).sort()];
	}, [assets]);

	/* A filter whose category has just been emptied (its last file deleted)
	   would otherwise show an empty list under a tab that no longer exists. */
	const active = categories.includes(category) ? category : 'all';
	const shown = assets.filter((a) => active === 'all' || a.category === active);

	if (assets.length === 0) {
		return (
			<>
				<SectionHead title="Assets & downloads" action={action} />
				<EmptyState
					icon={FileText}
					title="No files yet"
					hint={
						emptyHint ?? 'Brand books, dielines, templates and imagery all live here.'
					}
				/>
			</>
		);
	}

	return (
		<>
			<SectionHead
				title="Assets & downloads"
				meta={`${assets.length} ${assets.length === 1 ? 'file' : 'files'}`}
				action={action}
			/>

			{categories.length > 2 && (
				<div className="mb-3 flex flex-wrap gap-1.5">
					{categories.map((c) => (
						<button
							key={c}
							type="button"
							onClick={() => onCategoryChange(c)}
							className={cn(
								'rounded-[var(--r-2)] border px-3 py-1 text-xs capitalize transition-colors duration-[var(--dur-fast)]',
								active === c
									? 'border-line-2 bg-surface-fill text-text-1'
									: 'border-line-1 bg-transparent text-text-3 hover:text-text-1'
							)}
						>
							{c}
						</button>
					))}
				</div>
			)}

			<Card>
				{shown.map((asset, i) => {
					const openable = Boolean(asset.url && onOpen);
					return (
						<div
							key={asset.id}
							className={cn(
								'group flex items-center gap-3.5 px-4 py-3 transition-colors duration-[var(--dur-fast)] hover:bg-surface-hover',
								i > 0 && 'border-t border-line-1'
							)}
						>
							<button
								type="button"
								disabled={!openable}
								onClick={openable ? () => onOpen?.(asset.id) : undefined}
								className={cn(
									'bg-stripes flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-[var(--r-2)] border border-line-1 text-text-4',
									openable && 'cursor-pointer'
								)}
							>
								{asset.thumbnailUrl ? (
									<img
										src={asset.thumbnailUrl}
										alt=""
										className="h-full w-full object-contain"
									/>
								) : (
									<FileText size={14} />
								)}
							</button>

							<div className="min-w-0 flex-1">
								<div className="truncate text-sm font-medium text-text-1">
									{asset.name}
								</div>
								<div className="font-mono text-xs text-text-4">
									{fileMeta(asset.name, asset.mimeType, asset.size)}
								</div>
							</div>

							{renderActions && (
								<div className="flex shrink-0 items-center gap-1.5">
									{renderActions(asset)}
								</div>
							)}
						</div>
					);
				})}
			</Card>
		</>
	);
}
